import React, { FC, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as HeaderActions from '../actions/Header';
import * as Header from '../constants/Header';
import ScientificNursingCarePromotionForm from '../components/Organisms/ScientificNursingCarePromotionForm';
import {
  ScientificNursingCarePromotionAPI,
  createScientificNursingCarePromotionAPIInstance,
} from '../api/ScientificNursingCarePromotionAPI';
import PageLoader from '../components/Atoms/PageLoader';

interface ParamTypes {
  userId: string;
  evaluationId?: string;
}

const ScientificNursingCarePromotionFormContainer: FC = () => {
  const { userId, evaluationId } = useParams<ParamTypes>();
  const history = useHistory();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [api, setApi] = useState<ScientificNursingCarePromotionAPI | null>(null);

  // URLパスから編集モードかどうかを判定
  const isEditMode = !!evaluationId || window.location.pathname.includes('_edit/');

  // APIインスタンスの初期化
  useEffect(() => {
    const initApi = async () => {
      try {
        const apiInstance = createScientificNursingCarePromotionAPIInstance();
        if (apiInstance) {
          await apiInstance.setAxiosInstanceWithJwtToken();
          setApi(apiInstance);
        }
      } catch (err) {
        console.error('API初期化エラー:', err);
        setError('認証エラーが発生しました。再度ログインしてください。');
        setLoading(false);
      }
    };
    initApi();
  }, []);

  // 評価データを取得する関数
  const fetchEvaluationData = async () => {
    if (!evaluationId || !api) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // リクエスト前にJWTトークンを更新
      await api.setAxiosInstanceWithJwtToken();

      const response = await api.getEvaluationFunc(userId, evaluationId);
      setEvaluationData(response.data);
      setError(null);
    } catch (err) {
      console.error('評価データの取得に失敗しました:', err);

      // 認証エラーの場合は特別なメッセージを表示
      const error = err as any;
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setError('認証エラーが発生しました。再度ログインしてください。');
      } else {
        setError('評価データの取得に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  // 評価データを保存する関数
  const handleSave = async (formData: any) => {
    if (!api) {
      alert('APIが初期化されていません。再度ログインしてください。');
      return;
    }

    try {
      setLoading(true);

      // リクエスト前にJWTトークンを更新
      await api.setAxiosInstanceWithJwtToken();

      if (isEditMode && evaluationId) {
        // 編集モードの場合はPUTリクエスト
        await api.updateEvaluationFunc(userId, evaluationId, formData);
        alert('評価データを更新しました。');

        // 更新後、最新のデータを再取得
        await fetchEvaluationData();
      } else {
        // 新規作成モードの場合はPOSTリクエスト
        const response = await api.createEvaluationFunc(userId, formData);
        alert('評価データを保存しました。');

        // 新規作成の場合は、作成されたデータのIDを取得して編集モードに切り替え
        if (response.data && response.data.id) {
          history.replace(`/scientific_nursing_care_promotion_edit/${userId}/${response.data.id}`);
        }
      }
    } catch (err) {
      console.error('評価データの保存に失敗しました:', err);

      // 認証エラーの場合は特別なメッセージを表示
      const error = err as any;
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        alert('認証エラーが発生しました。再度ログインしてください。');
      } else {
        alert('評価データの保存に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  // キャンセル処理
  const handleCancel = () => {
    history.push(`/scientific_nursing_care_promotion/${userId}`);
  };

  // 複製処理
  const handleDuplicate = async (formData: any) => {
    if (!api) {
      alert('APIが初期化されていません。再度ログインしてください。');
      return;
    }

    try {
      setLoading(true);

      // リクエスト前にJWTトークンを更新
      await api.setAxiosInstanceWithJwtToken();

      // 新規作成として保存
      await api.createEvaluationFunc(userId, formData);
      alert('評価データを複製しました。');
      // 複製後、一覧画面に遷移
      history.push(`/scientific_nursing_care_promotion/${userId}`);
    } catch (err) {
      console.error('評価データの複製に失敗しました:', err);

      // 認証エラーの場合は特別なメッセージを表示
      const error = err as any;
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        alert('認証エラーが発生しました。再度ログインしてください。');
      } else {
        alert('評価データの複製に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  // 削除処理
  const handleDelete = async () => {
    if (!api || !evaluationId) {
      alert('APIが初期化されていないか、評価IDが不正です。');
      return;
    }

    // 削除確認
    if (!window.confirm('この評価データを削除してもよろしいですか？')) {
      return;
    }

    try {
      setLoading(true);

      // リクエスト前にJWTトークンを更新
      await api.setAxiosInstanceWithJwtToken();

      // 削除リクエスト
      await api.deleteEvaluationFunc(userId, evaluationId);
      alert('評価データを削除しました。');

      // 一覧画面に戻る
      history.push(`/scientific_nursing_care_promotion/${userId}`);
    } catch (err) {
      console.error('評価データの削除に失敗しました:', err);

      // 認証エラーの場合は特別なメッセージを表示
      const error = err as any;
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        alert('認証エラーが発生しました。再度ログインしてください。');
      } else {
        alert('評価データの削除に失敗しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = isEditMode ? '科学的介護推進に関する評価 - 編集' : '科学的介護推進に関する評価 - 新規作成';
    dispatch(HeaderActions.setPage(Header.SCIENTIFIC_NURSING_CARE_PROMOTION_FORM));

    // 編集モードの場合、評価データを取得
    if (isEditMode && api) {
      fetchEvaluationData();
    } else if (!isEditMode) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  if (error) {
    return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;
  }

  if (loading) {
    return <PageLoader />;
  }

  return (
    <ScientificNursingCarePromotionForm
      userId={userId}
      evaluationData={evaluationData}
      isEditMode={isEditMode}
      onSave={handleSave}
      onCancel={handleCancel}
      onDuplicate={handleDuplicate}
      onDelete={handleDelete}
    />
  );
};

export default ScientificNursingCarePromotionFormContainer;
